import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { Button } from '../../components/common/Common';
import { apiFetch, getFileUrl } from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import { ArrowLeft, Download, Loader2, Calendar } from 'lucide-react';
import { generateUserReportPDF } from './reportPDF';
import UserReportView from './UserReportView';
import './UserLeadsReport.css';

const formatDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const prettyDate = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const UserLeadsReport = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const [startDate, setStartDate] = useState(formatDate(firstDay));
  const [endDate, setEndDate] = useState(formatDate(now));

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);

  const loadDetail = async () => {
    setDetailLoading(true);
    try {
      const res = await apiFetch(`reports/users/${userId}?start_date=${startDate}&end_date=${endDate}`);
      if (res.success) setDetail(res.data);
      else showToast(res.message || 'Failed to load user report', 'error');
    } catch {
      showToast('Connection failed', 'error');
    }
    setDetailLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDetail(); }, [userId, startDate, endDate]);

  const handleDownload = () => {
    if (!detail) {
      showToast('Report not loaded yet', 'error');
      return;
    }
    try {
      generateUserReportPDF(detail);
    } catch {
      showToast('Failed to generate report', 'error');
    }
  };

  const user = detail?.user;
  const initial = (user?.full_name || user?.username || '?').trim().charAt(0).toUpperCase();

  return (
    <div>
      <Header
        title="User Leads Report"
        subtitle={user ? `${user.full_name || user.username} · ${user.role_name || ''}` : 'Loading…'}
        actions={
          <>
            <Button variant="primary" icon={Download} onClick={handleDownload} disabled={!detail}>
              Download Report
            </Button>
            <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/leads-report')}>
              Back to Reports
            </Button>
          </>
        }
      />

      <div className="page">
        <div className="mrpt">
          {/* Report header */}
          <div className="mrpt-head">
            <div className="mrpt-avatar">
              {user?.profile_image ? (
                <img src={getFileUrl(user.profile_image)} alt={user?.full_name || user?.username} />
              ) : (
                <span>{initial}</span>
              )}
            </div>
            <div className="mrpt-head__meta">
              <h2 className="mrpt-head__name">{user?.full_name || user?.username || 'Loading…'}</h2>
              <span className="mrpt-head__role">{user?.designation || user?.role_name || ''}</span>
              <span className="mrpt-head__range">{prettyDate(startDate)} - {prettyDate(endDate)}</span>
            </div>
            <div className="mrpt-daterange">
              <Calendar size={14} />
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mrpt-date" />
              <span className="mrpt-date-sep">→</span>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="mrpt-date" />
            </div>
          </div>

          {detailLoading && !detail ? (
            <div className="mrpt-loading"><Loader2 size={18} className="spin" /> Loading report…</div>
          ) : (
            <UserReportView detail={detail} />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserLeadsReport;
